/**
 * Admin Layout Route
 *
 * Layout wrapper for all admin pages with admin-specific sidebar navigation.
 * Includes role check -- redirects non-admins to dashboard.
 */

import {
  Image,
  Inline,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  Text,
  UserMenu,
} from '@cortex/ui';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import {
  ArrowLeft,
  Building2,
  FolderKanban,
  LayoutDashboard,
  Users,
} from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/providers/auth-provider';

export const Route = createFileRoute('/_app/admin')({
  beforeLoad: async ({ context }) => {
    const user = context.auth.user;
    if (user && user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  };

  return (
    <SidebarProvider className="bg-background h-screen">
      <Sidebar>
        <SidebarHeader>
          <Inline align="center" justify="between">
            <Link to="/" className="flex items-center px-[7%]">
              <Image
                src="/cortex-logo.webp"
                alt="Cortex"
                className="h-auto w-[84%]"
              />
            </Link>
            <SidebarTrigger />
          </Inline>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={false}
                    tooltip="Back to Dashboard"
                    onClick={() => navigate({ to: '/' })}
                  >
                    <ArrowLeft
                      className="h-5 w-5 shrink-0"
                      aria-hidden="true"
                    />
                    <Text as="span" truncate className="flex-1 text-left">
                      Back to Dashboard
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname === '/admin'}
                    tooltip="Admin Dashboard"
                    onClick={() => navigate({ to: '/admin' })}
                  >
                    <LayoutDashboard
                      className="h-5 w-5 shrink-0"
                      aria-hidden="true"
                    />
                    <Text as="span" truncate className="flex-1 text-left">
                      Admin Dashboard
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname.startsWith('/admin/organizations')}
                    tooltip="Organizations"
                    onClick={() => navigate({ to: '/admin/organizations' })}
                  >
                    <Building2
                      className="h-5 w-5 shrink-0"
                      aria-hidden="true"
                    />
                    <Text as="span" truncate className="flex-1 text-left">
                      Organizations
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname.startsWith('/admin/users')}
                    tooltip="Users"
                    onClick={() => navigate({ to: '/admin/users' })}
                  >
                    <Users className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <Text as="span" truncate className="flex-1 text-left">
                      Users
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname.startsWith('/admin/projects')}
                    tooltip="Projects"
                    onClick={() => navigate({ to: '/admin/projects' })}
                  >
                    <FolderKanban
                      className="h-5 w-5 shrink-0"
                      aria-hidden="true"
                    />
                    <Text as="span" truncate className="flex-1 text-left">
                      Projects
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          {user ? (
            <UserMenu
              user={{
                email: user.email,
                ...(user.name != null && { name: user.name }),
                ...(user.avatarUrl != null && { avatarUrl: user.avatarUrl }),
                ...(user.role != null && { role: user.role }),
                ...(user.organization != null && {
                  organization: user.organization,
                }),
              }}
              collapsed={false}
              onAdminClick={() => navigate({ to: '/admin' })}
              onSettingsClick={() => navigate({ to: '/settings' })}
              onLogoutClick={handleLogout}
              side="top"
              align="start"
            />
          ) : null}
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}
